"use client";

/**
 * Здоровье соединения — по фактам запросов, а не по догадкам.
 *
 * 🔴 Про VPN, ради которого это заводилось. Владелец просил плашку «при
 * использовании VPN возможны перебои» — и проверил вживую: прод стоит на
 * российском сервере, и через VPN приложение заметно тормозит. Надёжно
 * определить сам факт VPN в браузере НЕЛЬЗЯ: ни таймаут, ни иностранный IP
 * этого не доказывают, а платный сервис определения мы не подключаем. Поэтому
 * здесь нет попытки «обнаружить VPN» — есть только два измеримых СИМПТОМА:
 *
 *  1. Запрос не доехал вовсе (`fetch` бросил) — счётчик сбоев подряд, как и
 *     раньше. Отказ сервера сюда не попадает: это работающая сеть, и мешать
 *     его с обрывом значило бы пугать «проблемами со связью» человека,
 *     которому просто не хватает прав.
 *  2. Запросы идут ДОЛГО — медиана времени последних ответов заметно выше
 *     нормы. Один медленный запрос ничего не значит (сервер мог просто
 *     задуматься), а вот устойчиво высокая медиана — это и есть та самая
 *     деградация «через VPN до российского сервера».
 *
 * `netSignal()` сводит оба симптома в `{ slow, offline, medianMs, fails }` —
 * ими пользуется VpnChip.tsx: значок в шапке и карточка с объяснением. Это
 * единственный потребитель; прежняя плашка ConnectionHint.tsx удалена 20.09,
 * вместе с её более узким сигналом `shouldWarn`.
 *
 * Если однажды появится достоверный признак VPN (корпоративный прокси,
 * заголовок от инфраструктуры, платный детектор) — его место здесь, третьим
 * симптомом рядом с этими двумя. Флаг «точно VPN», который никто не выставлял,
 * убран 20.09 как мёртвый: он всегда был false и делал вид, что мы что-то
 * знаем.
 */

/** Сколько сбоев подряд считаем поводом сказать человеку. */
const THRESHOLD = 2;

/** Сколько последних замеров времени ответа храним — окно СКОЛЬЗИТ, а не растёт. */
const LATENCY_WINDOW = 12;

/**
 * Порог медианы окна: выше — «похоже, VPN». Обычный ответ нашего API — доли
 * секунды; 2.5с устойчиво держится только под VPN до дальнего сервера, а не
 * на разовом тормозе одного запроса (проверено владельцем вживую).
 */
const SLOW_MEDIAN_MS = 2500;

/** Меньше замеров в окне — рано судить: один-два медленных ответа не тенденция. */
const SLOW_MIN_SAMPLES = 5;

type State = { fails: number; latencies: number[] };

let state: State = { fails: 0, latencies: [] };
const listeners = new Set<(s: State) => void>();

function emit(): void {
  for (const fn of listeners) fn(state);
}

export function subscribeNet(fn: (s: State) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => {
    listeners.delete(fn);
  };
}

/** Запрос не доехал: fetch бросил. Ответ сервера с отказом сюда НЕ попадает. */
export function noteNetworkFailure(): void {
  state = { ...state, fails: state.fails + 1 };
  emit();
}

/** Запрос доехал — неважно, с каким ответом. Связь есть. */
export function noteNetworkOk(): void {
  if (state.fails === 0) return;
  state = { ...state, fails: 0 };
  emit();
}

/**
 * Сдвинуть окно замеров на один: добавить `ms`, а если окно переполнилось —
 * срезать самый старый. Чистая функция (без обращения к `state`) — тестам
 * незачем гонять реальный модуль, чтобы проверить, что окно не растёт
 * бесконечно и что старые значения вымываются новыми.
 */
export function pushLatency(window: number[], ms: number, max: number = LATENCY_WINDOW): number[] {
  const next = [...window, ms];
  return next.length > max ? next.slice(next.length - max) : next;
}

/** Медиана — устойчива к одному случайному выбросу, в отличие от среднего. */
export function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Замер времени одного запроса (успешного или нет — тормозит и то, и другое). */
export function noteLatency(ms: number): void {
  state = { ...state, latencies: pushLatency(state.latencies, ms) };
  emit();
}

export type NetSignal = {
  slow: boolean;
  offline: boolean;
  /**
   * Медиана окна, мс — чтобы плашка называла ЧИСЛО, а не просто пугала.
   * «Ответы идут за 3,4 с» человек проверит на глаз, «похоже, VPN» — нет.
   * 0 — замеров ещё мало.
   */
  medianMs: number;
  /** Сколько запросов подряд не доехало. */
  fails: number;
};

/**
 * Симптом «похоже, VPN» — по каждому из двух измеримых признаков отдельно
 * (VpnChip.tsx решает, что сказать, глядя на оба). Чистая функция от `s`, а
 * не от глобального `state`: тесты собирают произвольный `State` руками, не
 * гоняя модульный синглтон.
 */
export function netSignal(s: State): NetSignal {
  const enough = s.latencies.length >= SLOW_MIN_SAMPLES;
  const med = enough ? medianOf(s.latencies) : 0;
  return {
    offline: s.fails >= THRESHOLD,
    slow: enough && med > SLOW_MEDIAN_MS,
    medianMs: med,
    fails: s.fails,
  };
}

export type NetState = State;
